import { useState, type FC, useRef, type ChangeEvent } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { optionalProp } from '../core';
import {
  Drawer,
  Button,
  TextLink,
  Slider,
  AlertMessage,
  ButtonStyles,
  ButtonSize,
  ButtonTypes,
  TextLinkStyle,
  TextLinkIconPosition,
  TextLinkUnderlineStyle,
  IconStyle,
  Breakpoints,
  useBreakpoint,
} from '../index';
import { Theme } from '../types/enums';
import styles from './ProfileBannerDrawer.module.scss';
import type { ProfileBannerDrawerProps } from './types/propTypes';

const ProfileBannerDrawer: FC<ProfileBannerDrawerProps> = (props: ProfileBannerDrawerProps): JSX.Element => {
  const viewPortType = useBreakpoint();
  const {
    width,
    height,
    imageurl,
    isRoundCrop,
    isRotate,
    isRemoveImage,
    disableRemoveImgBtn = false,
    drawerTitle,
    border,
    showDrawer,
    uploadButtonTitle,
    saveButtonTitle,
    onSave,
    rotateButtonTitle,
    removeImageButtonTitle = '',
    description,
    maxFileSizeErrorMessage,
    invalidFileTypeErrorMessage,
    editorRef,
    onImageChange,
    onRemoveImage,
    crossOrigin,
    className = '',
  } = props;
  const [value, setValue] = useState(0);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [isError, setIsError] = useState(false);
  const [isFileTypesError, setIsFileTypesError] = useState(false);
  const [image, setImage] = useState<File | string>(imageurl);
  const inputRef = useRef<HTMLInputElement>(null);
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  // Currently only accept the following image file types as valid file types, others will return invalid file type error messages
  const VALID_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/svg+xml', 'image/png', 'image/tiff', 'image/gif'];
  const handleImageResize = (e: ChangeEvent<HTMLInputElement>) => {
    if (e && e.target && e.target.value) {
      setValue(Number(e.target.value));
      setScale(Number(e.target.value) + 1);
    }
  };
  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target && e.target.files && e.target.files.length > 0) {
      const file = e.target.files;
      const isFileSizeValid = (file[0]?.size as number) <= MAX_FILE_SIZE; // Valid File Size
      const isFileTypeValid = VALID_FILE_TYPES.includes(file[0]?.type as string); // Valid File Type

      if (isFileSizeValid && isFileTypeValid) {
        setImage(file[0] as File);
      }
      setIsError(!isFileSizeValid);
      setIsFileTypesError(!isFileTypeValid);
    }
  };

  const rotateImage = () => {
    setRotate(rotate + 90);
  };

  const borderRadius = isRoundCrop ? 600 : 0;

  return (
    <div data-testid="profile-banner-drawer-testid" className={styles['profile-banner-drawer']}>
      <Drawer
        bottomGap={0.5}
        footer={
          <div className={styles['footer-drawer']}>
            <div className={styles['footer-drawer-section']}>
              <Button
                data-testid="upload-testid"
                buttonStyle={ButtonStyles.OUTLINE}
                size={
                  viewPortType !== Breakpoints.XS && viewPortType !== Breakpoints.SM
                    ? ButtonSize.MEDIUM
                    : ButtonSize.SMALL
                }
                type={ButtonTypes.BUTTON}
                title={uploadButtonTitle}
                onClick={() => {
                  if (inputRef && inputRef.current) {
                    inputRef.current?.click();
                  }
                }}
              />
              <input
                ref={inputRef}
                data-testid="input-upload-testid"
                onChange={(_e) => handleFile(_e)}
                type="file"
                accept="image/*"
                className={styles['footer-input-upload']}
              />
            </div>
            <div className={styles['footer-drawer-section']}>
              <Button
                data-testid="crop-testid"
                title={saveButtonTitle}
                size={
                  viewPortType !== Breakpoints.XS && viewPortType !== Breakpoints.SM
                    ? ButtonSize.MEDIUM
                    : ButtonSize.SMALL
                }
                buttonStyle={ButtonStyles.SOLID}
                type={ButtonTypes.BUTTON}
                onClick={onSave}
              />
            </div>
          </div>
        }
        header={<div>{drawerTitle}</div>}
        closeIcon={true}
        className={className ?? ''}
        onClose={() => showDrawer(false)}
      >
        <>
          <div className={styles['profile-avatar-wrapper']}>
            <div className={styles['profile-avatar-row']}>
              <AvatarEditor
                ref={editorRef}
                data-testid="avatar-editor-testid"
                crossOrigin={crossOrigin}
                image={image}
                width={width}
                height={height}
                borderRadius={borderRadius}
                border={border}
                color={[255, 255, 255, 0.6]} // RGBA
                rotate={rotate}
                scale={scale}
                {...optionalProp('onImageChange', onImageChange)}
                style={{
                  width: '100%',
                  height: 'auto',
                }}
              />
            </div>
            <div className={styles['profile-avatar-row']}>
              <Slider
                min="0"
                max="5"
                step="0.1"
                value={value}
                data-testid="slider-testid"
                onChange={(_e) => {
                  handleImageResize(_e);
                }}
              />
            </div>
            {isRotate && (
              <div className={styles['profile-avatar-row']}>
                <div className={styles['profile-rotate-container']}>
                  <div
                    onClick={rotateImage}
                    onKeyDown={rotateImage}
                    className={styles['profile-rotate']}
                    role="presentation"
                  >
                    <TextLink
                      text={rotateButtonTitle}
                      onClick={rotateImage}
                      textLinkStyle={TextLinkStyle.ICON}
                      theme={Theme.PURPLE}
                      iconPosition={TextLinkIconPosition.LEFT}
                      leftIcon={{
                        iconName: 'rotate',
                        iconStyle: IconStyle.SOLID,
                      }}
                      underline={TextLinkUnderlineStyle.NOHOVER}
                      data-testid="profile-rotate-button-testid"
                    />
                  </div>

                  {isRotate && isRemoveImage && (
                    <div className={styles['profile-divider']} data-testid="profile-divider-testid">
                      |
                    </div>
                  )}

                  {isRotate && isRemoveImage && (
                    <div onKeyDown={onRemoveImage} className={styles['profile-remove-image']} role="presentation">
                      <TextLink
                        text={removeImageButtonTitle}
                        onClick={onRemoveImage}
                        textLinkStyle={TextLinkStyle.ICON}
                        theme={Theme.PURPLE}
                        disabled={disableRemoveImgBtn}
                        iconPosition={TextLinkIconPosition.LEFT}
                        leftIcon={{
                          iconName: 'trash-can',
                          iconStyle: IconStyle.SOLID,
                        }}
                        underline={TextLinkUnderlineStyle.NOHOVER}
                        data-testid="profile-remove-image-button-testid"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {!isRotate && isRemoveImage && (
              <div className={styles['profile-avatar-row']}>
                <div className={styles['profile-rotate-container']}>
                  <div onKeyDown={onRemoveImage} className={styles['profile-remove-image']} role="presentation">
                    <TextLink
                      text={removeImageButtonTitle}
                      onClick={onRemoveImage}
                      textLinkStyle={TextLinkStyle.ICON}
                      theme={Theme.PURPLE}
                      disabled={disableRemoveImgBtn}
                      iconPosition={TextLinkIconPosition.LEFT}
                      leftIcon={{
                        iconName: 'trash-can',
                        iconStyle: IconStyle.SOLID,
                      }}
                      underline={TextLinkUnderlineStyle.NOHOVER}
                      data-testid="profile-remove-image-button-testid"
                    />
                  </div>
                </div>
              </div>
            )}
            <div className={`${styles['profile-avatar-row']} ${styles['profile-content']}`}>{description}</div>
            {isError && (
              <AlertMessage theme={Theme.ERROR} className={styles['profile-error-state']} icon={'triangle-exclamation'}>
                {maxFileSizeErrorMessage}
              </AlertMessage>
            )}
            {isFileTypesError && (
              <AlertMessage theme={Theme.ERROR} className={styles['profile-error-state']} icon={'triangle-exclamation'}>
                {invalidFileTypeErrorMessage}
              </AlertMessage>
            )}
          </div>
        </>
      </Drawer>
    </div>
  );
};

export default ProfileBannerDrawer;
